import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import GalleryToolbar, { PublisherTemplate, DeviceMode } from '@/components/gallery/GalleryToolbar';
import GalleryAdSlot, { AdSlotConfig } from '@/components/gallery/GalleryAdSlot';
import { fetchGalleryItemBySlug } from '@/lib/cms';

const STORAGE_KEY = 'adventive-preview-state';

const defaultAdSlots: AdSlotConfig[] = [
  { id: 'header-1x1', name: 'Header 1×1', width: 1, height: 1, label: 'Header 1×1', tag: '' },
  { id: 'full-width-header', name: 'Full Width Header', width: 970, height: 250, label: 'Full Width Header (Top of Page) — 970×250', tag: '' },
  { id: 'full-width-banner', name: 'Full Width Banner', width: 970, height: 250, label: 'Full Width Banner (Below Nav) — 970×250', tag: '' },
  { id: 'content-width-banner', name: 'Content Width Banner', width: 728, height: 90, label: 'Content Width Banner — 728×90', tag: '' },
  { id: '300x250', name: '300×250', width: 300, height: 250, label: '300×250', tag: '' },
  { id: '300x600', name: '300×600', width: 300, height: 600, label: '300×600', tag: '' },
  { id: 'between-article', name: 'Between Article', width: 728, height: 90, label: 'Between Article — 728×90', tag: '' },
  { id: 'footer-1x1', name: 'Footer 1×1', width: 1, height: 1, label: 'Footer 1×1', tag: '' },
];

const DEVICE_WIDTHS: Record<DeviceMode, string> = {
  desktop: '100%',
  tablet: '768px',
  mobile: '390px',
};

interface TemplateData {
  siteName: string;
  tagline: string;
  domain: string;
  navItems: string[];
  heroCategory: string;
  heroTitle: string;
  heroAuthor: string;
  featuredArticles: { category: string; title: string; author: string }[];
  gridArticles: { category: string; title: string; author: string }[];
  listArticles: { category: string; title: string; author: string }[];
}


const templateConfig: Record<PublisherTemplate, TemplateData> = {
  news: {
    siteName: 'MERIDIAN',
    tagline: 'Stories From Every Direction',
    domain: 'meridianmag.com',
    navItems: ['Destinations', 'Trip Ideas', 'Food & Drink', 'Culture', 'Outdoor', 'Wellness', 'Sustainability', 'News'],
    heroCategory: 'DESTINATIONS',
    heroTitle: 'The 30 Best Places to Travel in 2026',
    heroAuthor: 'By the Editors of Meridian',
    featuredArticles: [
      { category: 'TRIP IDEAS', title: 'Why This Hidden Greek Island Should Be Your Next Escape', author: 'Sarah Chen' },
      { category: 'FOOD & DRINK', title: 'The World\'s Best New Restaurants, According to Chefs', author: 'Marcus Rivera' },
      { category: 'CULTURE', title: 'How to Experience Tokyo Like a Local', author: 'Yuki Tanaka' },
    ],
    gridArticles: [
      { category: 'OUTDOOR', title: 'The Most Stunning Hiking Trails You\'ve Never Heard Of', author: 'James Park' },
      { category: 'WELLNESS', title: 'The Rise of Slow Travel: Why Less Is More', author: 'Elena Rossi' },
      { category: 'SUSTAINABILITY', title: 'How Regenerative Tourism Is Changing the Way We Travel', author: 'David Okafor' },
      { category: 'DESTINATIONS', title: 'A Perfect Weekend in Lisbon', author: 'Ana Silva' },
    ],
    listArticles: [
      { category: 'NEWS', title: 'New Direct Flights Make These Destinations More Accessible Than Ever', author: 'Travel Desk' },
      { category: 'TRIP IDEAS', title: 'The Best Hotels Opening in 2026', author: 'Editorial Staff' },
      { category: 'CULTURE', title: 'A Guide to the World\'s Most Underrated Museums', author: 'Claire Fontaine' },
      { category: 'FOOD & DRINK', title: 'Where to Eat in Mexico City Right Now', author: 'Roberto Garza' },
      { category: 'OUTDOOR', title: 'The National Parks You Should Visit This Spring', author: 'Alex Mercer' },
    ],
  },
  lifestyle: {
    siteName: 'ATLAS',
    tagline: 'A Briefing on Global Affairs, Business, Culture & Design',
    domain: 'atlasreport.com',
    navItems: ['Affairs', 'Business', 'Culture', 'Design', 'Edits', 'Travel', 'Print', 'Radio'],
    heroCategory: 'DESIGN',
    heroTitle: 'The Architects Redefining How We Live and Work',
    heroAuthor: 'By the Editors',
    featuredArticles: [
      { category: 'CULTURE', title: 'Why Copenhagen Is the World\'s Most Liveable City', author: 'Lars Andersen' },
      { category: 'BUSINESS', title: 'The Small Brands Making Big Waves in Retail', author: 'Mika Sato' },
      { category: 'AFFAIRS', title: 'A New Vision for Urban Planning in Southeast Asia', author: 'Priya Sharma' },
    ],
    gridArticles: [
      { category: 'EDITS', title: 'The Objects That Define Modern Japanese Craft', author: 'Kenji Yamamoto' },
      { category: 'TRAVEL', title: 'A Weekend Guide to Porto', author: 'Sofia Costa' },
      { category: 'DESIGN', title: 'How Scandinavian Furniture Is Evolving', author: 'Erik Lindqvist' },
      { category: 'CULTURE', title: 'The Independent Bookshops Worth Travelling For', author: 'Emma Wright' },
    ],
    listArticles: [
      { category: 'BUSINESS', title: 'Why More Companies Are Moving to Smaller Cities', author: 'Business Desk' },
      { category: 'AFFAIRS', title: 'The Diplomats Shaping a New Era of Cooperation', author: 'Affairs Desk' },
      { category: 'DESIGN', title: 'A New Generation of Ceramic Artists', author: 'Design Desk' },
      { category: 'CULTURE', title: 'The Festivals That Define Summer 2026', author: 'Culture Desk' },
      { category: 'EDITS', title: 'Our Favourite New Openings This Month', author: 'Editorial Staff' },
    ],
  },
  sports: {
    siteName: 'SUMMIT',
    tagline: 'Adventure, Sport & the Outdoors',
    domain: 'summitjournal.com',
    navItems: ['Climbing', 'Running', 'Cycling', 'Surfing', 'Skiing', 'Expeditions', 'Gear', 'Stories'],
    heroCategory: 'EXPEDITIONS',
    heroTitle: 'Crossing Patagonia: A 40-Day Journey on Foot',
    heroAuthor: 'By the Editors',
    featuredArticles: [
      { category: 'CLIMBING', title: 'The New Route That\'s Rewriting Alpine History', author: 'Will Stanhope' },
      { category: 'SURFING', title: 'Finding Perfect Waves in Remote Indonesia', author: 'Maya Lin' },
      { category: 'RUNNING', title: 'What Ultra-Running Teaches You About Life', author: 'Courtney Wells' },
    ],
    gridArticles: [
      { category: 'CYCLING', title: 'The Most Beautiful Bike Routes in Europe', author: 'Marco Bellini' },
      { category: 'SKIING', title: 'Backcountry Skiing in Japan\'s Hidden Mountains', author: 'Yuki Sato' },
      { category: 'GEAR', title: 'The Only Gear You Actually Need', author: 'Gear Editors' },
      { category: 'STORIES', title: 'The Woman Who Kayaked the Amazon Alone', author: 'Sarah Outen' },
    ],
    listArticles: [
      { category: 'CLIMBING', title: 'New Speed Record Set on El Capitan', author: 'Climbing Desk' },
      { category: 'RUNNING', title: 'The Trail Races Worth Travelling For in 2026', author: 'Running Desk' },
      { category: 'EXPEDITIONS', title: 'Solo Crossing of the Antarctic Plateau Completed', author: 'Expedition Desk' },
      { category: 'GEAR', title: 'The Best Trail Shoes We Tested This Year', author: 'Gear Desk' },
      { category: 'SURFING', title: 'Big Wave Season Delivers Record Swells', author: 'Surf Desk' },
    ],
  },
  technology: {
    siteName: 'SIGNAL',
    tagline: 'The Future Is Already Here',
    domain: 'signalmedia.com',
    navItems: ['Science', 'AI', 'Culture', 'Gear', 'Business', 'Security', 'Politics', 'Ideas'],
    heroCategory: 'AI',
    heroTitle: 'The Quiet Revolution Happening Inside Your Phone',
    heroAuthor: 'By the Editors',
    featuredArticles: [
      { category: 'SCIENCE', title: 'The Breakthrough That Could Change Medicine Forever', author: 'Dr. Emily Zhang' },
      { category: 'CULTURE', title: 'How the Internet Became a Place Again', author: 'Kate Knibbs' },
      { category: 'GEAR', title: 'The Best Laptops for Every Kind of Work', author: 'Gear Team' },
    ],
    gridArticles: [
      { category: 'BUSINESS', title: 'The Startups Building the Next Internet', author: 'Lauren Goode' },
      { category: 'SECURITY', title: 'Why Your Password Manager Isn\'t Enough Anymore', author: 'Lily Newman' },
      { category: 'IDEAS', title: 'What Happens When AI Starts Making Art That Matters', author: 'Jason Parham' },
      { category: 'POLITICS', title: 'The Fight Over Who Controls the Algorithm', author: 'Gilad Edelman' },
    ],
    listArticles: [
      { category: 'AI', title: 'The Next Big Move Could Reshape the Industry', author: 'AI Desk' },
      { category: 'SCIENCE', title: 'A New Kind of Battery Could Power Everything', author: 'Science Desk' },
      { category: 'GEAR', title: 'The Gadgets That Actually Made Our Lives Better', author: 'Gear Desk' },
      { category: 'CULTURE', title: 'The Streaming Wars Are Over. Here\'s Who Won.', author: 'Culture Desk' },
      { category: 'SECURITY', title: 'The Biggest Data Breach You Haven\'t Heard About', author: 'Security Desk' },
    ],
  },
};

/* Reusable blur style for editorial content — heavier blur.
   position+zIndex keep these below ad slots: filter:blur() creates a new
   stacking context, and without an explicit z-index it can float above ads. */
const editorialBlur: React.CSSProperties = { filter: 'blur(3.5px)', pointerEvents: 'none' as const, userSelect: 'none' as const, position: 'relative' as const, zIndex: 0 };

/* Mock article body text for Interscroller format — gives enough scroll
   height above and below the ad for the scroll-reveal effect to work. */
const MOCK_ARTICLE_PARAGRAPHS_ABOVE = [
  "The world shifts quietly beneath your feet when you travel with your eyes open. In the narrow streets of old quarters, in the vast open plains where the horizon stretches to infinity, in the crowded markets and silent temples — every place carries a story that began long before you arrived and will continue long after you leave.",
  "What draws people outward is rarely just the destination. It is the accumulation of small, unremarkable details: the quality of light at a particular hour, the cadence of a language heard through an open window, the way a single meal can carry the full weight of a season and a culture and all the hands that shaped it.",
  "There is a version of every journey that exists only in the planning — neat, organised, certain. Then there is the actual journey, which is always something else entirely. More demanding and more generous. Messier. More real. And always, inevitably, richer than the version you imagined sitting at home with a map.",
];

const MOCK_ARTICLE_PARAGRAPHS_BELOW = [
  "The moments you carry home are rarely the ones you expected. They happen at the margins: a conversation that begins over a missed turn, a discovery made entirely by accident, a landscape that appears without warning around a bend in the road and stops you completely in your tracks.",
  "By the time you return, something has settled inside you, though you may not be able to say exactly what. The familiar looks slightly different. The ordinary seems, briefly and beautifully, like a form of grace — the kind you only notice after you have been away long enough to miss it.",
  "People have always moved — for food and shelter, for trade and connection, for the kind of curiosity that cannot be satisfied by remaining still. To travel is not exceptional. It is one of the oldest and most human of all impulses, written into us long before borders existed to complicate the matter.",
  "There is something about arriving somewhere new that recalibrates your sense of time. Days feel longer. The hours have weight. You notice things you would walk past at home without a second glance — the pattern of tiles on a floor, the way afternoon light falls across a particular wall, the sound a city makes when it wakes up.",
  "The traveller learns, sooner or later, that preparation and spontaneity are not opposites. The best journeys are ones where you know enough to feel oriented, but not so much that surprise is impossible. Leave room in the itinerary for the unplanned hour, the detour that takes all afternoon, the conversation that rewrites the rest of the day.",
  "Every destination contains dozens of possible trips. The one you take depends on your mood, your budget, your companions, the season, and a hundred small decisions that compound invisibly as you go. This is why the same city can feel completely different on a second or third visit — you have changed, and so has your ability to see it.",
  "Travelling well is not about covering the most distance or checking the most places off a list. It is about the quality of attention you bring. Some people see more in a single afternoon in one neighbourhood than others absorb in two weeks of efficient sightseeing. The pace that suits you is the right pace.",
  "Somewhere along the way, travel becomes less about escape and more about understanding. The further you go, the more clearly you see where you started from. Distance is not just geographical. It is also a way of gaining perspective on the life you left behind — its habits, its assumptions, its particular texture of the ordinary.",
  "The great cities of the world reward patience. They are not best understood from the top of an observation deck or through the windows of a tour bus, but from street level, on foot, at the speed at which you can actually take things in. The city reveals itself slowly, in layers, to those willing to walk and look and return.",
  "What you remember from a long journey is never quite what you expected to remember. The famous sights recede. What stays is smaller and stranger: a smell, an overheard phrase in a language you don't speak, the particular quality of silence in a place you will almost certainly never return to. This is the gift of travel, and it cannot be planned for.",
];


interface PreviewRouteState {
  adTag?: string;
  adSlotId?: string;
  itemTitle?: string;
}

// Encode slot tags to a URL-safe base64 string (only non-empty tags)
function encodeSlotsParam(slots: AdSlotConfig[]): string | null {
  const map: Record<string, string> = {};
  slots.forEach(s => { if (s.tag) map[s.id] = s.tag; });
  if (Object.keys(map).length === 0) return null;
  try {
    const bytes = new TextEncoder().encode(JSON.stringify(map));
    return btoa(String.fromCharCode(...bytes));
  } catch { return null; }
}

function decodeSlotsParam(encoded: string): Record<string, string> | null {
  try {
    const bytes = Uint8Array.from(atob(encoded), c => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch { return null; }
}

const GalleryPreview: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isFullScreen = searchParams.get('fullscreen') === 'true';
  const isFrame = searchParams.get('frame') === 'true';

  // Initialise from URL params so links are shareable / directly navigable
  const [isEditing, setIsEditing] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState<PublisherTemplate>(
    () => (searchParams.get('template') as PublisherTemplate) || 'news'
  );
  const [adSlots, setAdSlots] = useState<AdSlotConfig[]>(() => {
    const slotsParam = searchParams.get('slots');
    if (!slotsParam) return defaultAdSlots;
    const tagMap = decodeSlotsParam(slotsParam);
    if (!tagMap) return defaultAdSlots;
    return defaultAdSlots.map(s => ({ ...s, tag: tagMap[s.id] ?? '' }));
  });
  const [adFormat, setAdFormat] = useState<string>('');
  const [externalEditSlotId, setExternalEditSlotId] = useState<string | null>(null);
  const [deviceMode, setDeviceMode] = useState<DeviceMode>(
    () => (searchParams.get('device') as DeviceMode) || 'desktop'
  );
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const config = templateConfig[activeTemplate];

  // Create a container for the header-1x1 portal and prepend it as the very
  // first child of <body> — Cinema ads require placement immediately after <body>.
  const [headerPortalEl] = useState<HTMLDivElement>(() => {
    const el = document.createElement('div');
    el.id = 'adslot-header-1x1';
    return el;
  });
  useEffect(() => {
    document.body.insertBefore(headerPortalEl, document.body.firstChild);
    return () => { headerPortalEl.remove(); };
  }, [headerPortalEl]);

  // Sync shareable state to URL params (replace so back-button isn't polluted)
  useEffect(() => {
    if (isFrame || isFullScreen) return;
    const params: Record<string, string> = {};
    if (activeTemplate !== 'news') params.template = activeTemplate;
    if (deviceMode !== 'desktop') params.device = deviceMode;
    const itemSlug = searchParams.get('item');
    if (itemSlug) {
      // Keep human-readable ?item=slug URL instead of encoding slots blob
      params.item = itemSlug;
    } else {
      const encoded = encodeSlotsParam(adSlots);
      if (encoded) params.slots = encoded;
    }
    setSearchParams(params, { replace: true });
  }, [activeTemplate, deviceMode, adSlots, isFrame, isFullScreen, setSearchParams]);

  // Frame mode: receive state from parent via postMessage
  useEffect(() => {
    if (!isFrame) return;
    const handler = (e: MessageEvent) => {
      if (e.data?.type !== 'PREVIEW_STATE') return;
      const { adSlots: a, activeTemplate: t, isEditing: ed } = e.data;
      if (a) setAdSlots(a);
      if (t) setActiveTemplate(t);
      if (ed !== undefined) setIsEditing(ed);
    };
    window.addEventListener('message', handler);
    // Signal ready so parent sends initial state
    window.parent.postMessage({ type: 'FRAME_READY' }, '*');
    return () => window.removeEventListener('message', handler);
  }, [isFrame]);


  // Restore state from sessionStorage in fullscreen mode
  useEffect(() => {
    if (isFullScreen) {
      try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.adSlots) setAdSlots(parsed.adSlots);
          if (parsed.activeTemplate) setActiveTemplate(parsed.activeTemplate);
        }
      } catch { /* ignore */ }
    }
  }, [isFullScreen]);

  // Auto-populate ad slot from route state (when navigating from Gallery listing)
  useEffect(() => {
    const state = location.state as PreviewRouteState | null;
    if (state?.adTag && state?.adSlotId) {
      setAdSlots(prev =>
        prev.map(slot =>
          slot.id === state.adSlotId ? { ...slot, tag: state.adTag! } : slot
        )
      );
    }
  }, [location.state]);

  // Load a single gallery item by slug when ?item=slug is present.
  // This keeps preview URLs human-readable (no encoded slots blob).
  useEffect(() => {
    const itemSlug = searchParams.get('item');
    if (!itemSlug || isFrame) return;
    fetchGalleryItemBySlug(itemSlug).then(item => {
      if (!item?.adventive_ad_tag || !item?.preview_ad_slot) return;
      if (item.ad_format) setAdFormat(item.ad_format);
      let companions: Record<string, string> = {};
      if (item.companion_ad_slots) {
        try { companions = JSON.parse(item.companion_ad_slots); } catch { /* ignore malformed JSON */ }
      }
      setAdSlots(prev => prev.map(s => {
        if (s.id === item.preview_ad_slot) return { ...s, tag: item.adventive_ad_tag };
        if (companions[s.id]) return { ...s, tag: companions[s.id] };
        return s;
      }));
    }).catch(() => { /* silently ignore fetch errors */ });
  }, [searchParams, isFrame, isFullScreen]);

  const handleUpdateTag = useCallback((slotId: string, tag: string) => {
    setAdSlots(prev => prev.map(slot => slot.id === slotId ? { ...slot, tag } : slot));
  }, []);
  const handleResetAll = useCallback(() => { setAdSlots(defaultAdSlots.map(s => ({ ...s, tag: '' }))); }, []);

  // In frame mode, bubble configure requests up to the parent toolbar
  const handleConfigureTag = useCallback((slotId: string) => {
    if (isFrame) {
      window.parent.postMessage({ type: 'CONFIGURE_TAG', slotId }, '*');
    } else {
      setExternalEditSlotId(slotId);
    }
  }, [isFrame]);

  const handleClearExternalEdit = useCallback(() => { setExternalEditSlotId(null); }, []);
  const getSlot = (id: string) => adSlots.find(s => s.id === id)!;

  const handleFullScreen = useCallback(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ adSlots, activeTemplate }));
    window.open(`${window.location.pathname}?fullscreen=true`, '_blank');
  }, [adSlots, activeTemplate]);

  // Keep a ref with current state so message handlers never read stale closures
  const frameStateRef = useRef({ adSlots, activeTemplate, isEditing });
  useEffect(() => {
    frameStateRef.current = { adSlots, activeTemplate, isEditing };
  }, [adSlots, activeTemplate, isEditing]);

  const sendStateToFrame = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'PREVIEW_STATE', ...frameStateRef.current }, '*'
    );
  }, []);

  // Push state to iframe whenever it changes
  useEffect(() => {
    if (isFrame || isFullScreen) return;
    sendStateToFrame();
  }, [adSlots, activeTemplate, isEditing, isFrame, isFullScreen, sendStateToFrame]);

  // Listen for messages from the iframe
  useEffect(() => {
    if (isFrame || isFullScreen) return;
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'FRAME_READY') {
        // iframe just loaded — send current state (via ref, never stale)
        sendStateToFrame();
      } else if (e.data?.type === 'CONFIGURE_TAG') {
        setExternalEditSlotId(e.data.slotId);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [isFrame, isFullScreen, sendStateToFrame]);

  const deviceWidth = DEVICE_WIDTHS[deviceMode];

  const isWallpaper = adFormat === 'Wallpaper';
  const isInterscroller = adFormat === 'Interscroller';

  // For Wallpaper format, strip position/z-index from editorial blur so these
  // elements don't create their own stacking contexts within #wrapper — keeps
  // the ad slot z-index:200 rule from being obscured by sibling stacking contexts.
  const activeEditorialBlur: React.CSSProperties = isWallpaper
    ? { filter: 'blur(3.5px)', pointerEvents: 'none', userSelect: 'none' }
    : editorialBlur;

  // Publisher site content — shared between normal and fullscreen modes
  const publisherSite = (
    <div id="wrapper" className="bg-white min-h-screen" style={{ fontFamily: 'Georgia, "Times New Roman", serif', position: 'relative', zIndex: 1, ...(isWallpaper ? { maxWidth: '1024px', margin: '0 auto' } : {}) }}>
      {/* Header 1×1 — portaled into a div prepended as the first child of <body>
          so Cinema ads (which require placement immediately after the body open
          tag) execute in the correct position. headerPortalEl is inserted via
          useEffect above. */}
      {createPortal(
        <GalleryAdSlot config={getSlot('header-1x1')} onConfigureTag={handleConfigureTag} isEditing={isEditing} className="adslot-header-1x1" scriptTarget={headerPortalEl} />,
        headerPortalEl
      )}

      {/* Ensure ad slots always sit above editorial content.
          filter:blur() on editorial divs creates stacking contexts that can
          trap ad creatives underneath — this overrides that. */}
      <style>{`
        [id^="adslot-"] { position: relative; z-index: 200; }
        [id^="adslot-"] > * { position: relative; z-index: 200; }
        #advWallContainer { position: fixed !important; top: 0 !important; left: 0 !important; width: 100vw !important; height: 100vh !important; }
        html, body { background: ${isWallpaper ? 'transparent' : 'white'} !important; }
      `}</style>

      {/* Full Width Header (Top of Page) — only shown when slot has an active tag */}
      {getSlot('full-width-header').tag && (
        <div id="adslot-full-width-header" className="bg-white border-b border-black/5" style={{ maxWidth: '100vw', overflow: 'hidden' }}>
          <GalleryAdSlot config={getSlot('full-width-header')} onConfigureTag={handleConfigureTag} isEditing={isEditing} className="adslot-full-width-header" unconstrained />
        </div>
      )}

      {/* Publisher Header - Minimal, clean — NOT blurred */}
      <header className="border-b border-black/10" style={isWallpaper ? {} : { position: 'relative', zIndex: 1 }}>
        {/* Logo / Masthead */}
        <div className="py-6 text-center">
          <h2 className="text-4xl md:text-5xl font-normal tracking-[0.08em] text-black" style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontWeight: 400 }}>
            {config.siteName}
          </h2>
          <p className="text-[10px] uppercase tracking-[0.3em] text-black/40 mt-1.5" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            {config.tagline}
          </p>
        </div>

        {/* Navigation */}
        <nav className="border-t border-black/10 px-8 overflow-x-auto">
          <div className="flex items-center justify-center gap-0 flex-wrap">
            {config.navItems.map((item) => (
              <button
                key={item}
                className="px-4 py-3 text-[11px] uppercase tracking-[0.15em] text-black/60 hover:text-black transition-colors whitespace-nowrap"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                {item}
              </button>
            ))}
          </div>
        </nav>
      </header>

      {/* Full Width Banner (Below Nav) */}
      {getSlot('full-width-banner').tag && (
        <div id="adslot-full-width-banner" className="py-5 flex justify-center bg-white border-b border-black/5">
          <GalleryAdSlot config={getSlot('full-width-banner')} onConfigureTag={handleConfigureTag} isEditing={isEditing} className="adslot-full-width-banner" />
        </div>
      )}

      {/* Content Width Banner — below nav */}
      {(!isWallpaper || getSlot('content-width-banner').tag) && <div id="adslot-content-width-banner" className="py-5 bg-white border-b border-black/5 px-8">
        <div className="flex justify-center">
          <GalleryAdSlot config={getSlot('content-width-banner')} onConfigureTag={handleConfigureTag} isEditing={isEditing} className="adslot-content-width-banner" />
        </div>
      </div>}



      {/* Main Content — article reading view for Interscroller, grid+sidebar for everything else */}
      {isInterscroller ? (

        /* ── Interscroller: single-column article with ad embedded in body text ── */
        <div className="px-8 py-10">
          <div className="max-w-2xl mx-auto">

            {/* Article header — BLURRED */}
            <div className="mb-8" style={activeEditorialBlur}>
              <span
                className="text-[10px] uppercase tracking-[0.2em] text-black/40 block mb-3"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                {config.heroCategory}
              </span>
              <h1 className="text-[2.25rem] leading-[1.2] font-normal text-black mb-4">
                {config.heroTitle}
              </h1>
              <div className="flex items-center gap-3 border-t border-black/10 pt-4">
                <span className="text-sm text-black/50" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                  {config.heroAuthor}
                </span>
                <span className="text-black/20">·</span>
                <span className="text-sm text-black/40" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                  8 min read
                </span>
              </div>
            </div>

            {/* Hero image — BLURRED */}
            <div className="w-full aspect-[16/9] bg-neutral-100 mb-8" style={activeEditorialBlur} />

            {/* Body text above ad — BLURRED */}
            <div style={activeEditorialBlur}>
              {MOCK_ARTICLE_PARAGRAPHS_ABOVE.map((para, i) => (
                <p key={i} className="text-base leading-[1.85] text-black/80 mb-6">
                  {para}
                </p>
              ))}
            </div>

            {/* Interscroller ad slot — block container, full column width, no flex centering */}
            <div id="adslot-between-article" className="w-full my-2">
              <GalleryAdSlot config={getSlot('between-article')} onConfigureTag={handleConfigureTag} isEditing={isEditing} className="adslot-between-article" fullWidth />
            </div>

            {/* Body text below ad — BLURRED */}
            <div style={activeEditorialBlur}>
              {MOCK_ARTICLE_PARAGRAPHS_BELOW.map((para, i) => (
                <p key={i} className="text-base leading-[1.85] text-black/80 mb-6">
                  {para}
                </p>
              ))}
            </div>

          </div>
        </div>

      ) : (

        /* ── Standard: grid layout + sidebar ── */
        <div className="px-8 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Main Column — 2/3 */}
            <div className="lg:col-span-2">
              {/* Section label — BLURRED */}
              <div className="border-b border-black pb-2 mb-8" style={activeEditorialBlur}>
                <h3
                  className="text-[11px] uppercase tracking-[0.2em] text-black"
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif', fontWeight: 600 }}
                >
                  Latest Stories
                </h3>
              </div>

              {/* Article Grid - 2 columns — BLURRED */}
              <div className="grid sm:grid-cols-2 gap-8 mb-10" style={activeEditorialBlur}>
                {config.gridArticles.slice(0, 2).map((article, i) => (
                  <article key={i} className="group cursor-pointer">
                    <div className="w-full aspect-[3/2] bg-neutral-100 mb-3" />
                    <span
                      className="text-[10px] uppercase tracking-[0.2em] text-black/40"
                      style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                    >
                      {article.category}
                    </span>
                    <h4 className="text-base leading-[1.35] text-black mt-1" style={{ fontWeight: 400 }}>
                      {article.title}
                    </h4>
                    <p
                      className="text-[11px] text-black/40 mt-1.5"
                      style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                    >
                      {article.author}
                    </p>
                  </article>
                ))}
              </div>

              {/* Between Article Ad — NOT blurred */}
              {(!isWallpaper || getSlot('between-article').tag) && (
                <div id="adslot-between-article" className="py-5 flex justify-center border-t border-b border-black/5 mb-10">
                  <GalleryAdSlot config={getSlot('between-article')} onConfigureTag={handleConfigureTag} isEditing={isEditing} className="adslot-between-article" />
                </div>
              )}

              {/* More articles - 2 columns — BLURRED */}
              <div className="grid sm:grid-cols-2 gap-8 mb-10" style={activeEditorialBlur}>
                {config.gridArticles.slice(2).map((article, i) => (
                  <article key={i} className="group cursor-pointer">
                    <div className="w-full aspect-[3/2] bg-neutral-100 mb-3" />
                    <span
                      className="text-[10px] uppercase tracking-[0.2em] text-black/40"
                      style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                    >
                      {article.category}
                    </span>
                    <h4 className="text-base leading-[1.35] text-black mt-1" style={{ fontWeight: 400 }}>
                      {article.title}
                    </h4>
                    <p
                      className="text-[11px] text-black/40 mt-1.5"
                      style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                    >
                      {article.author}
                    </p>
                  </article>
                ))}
              </div>

              {/* Between Article Ad (second position) — NOT blurred */}
              {(!isWallpaper || getSlot('between-article').tag) && (
                <div id="adslot-between-article-2" className="py-5 flex justify-center border-t border-b border-black/5 mb-10">
                  <GalleryAdSlot config={{ ...getSlot('between-article'), tag: '' }} onConfigureTag={handleConfigureTag} isEditing={isEditing} className="adslot-between-article-2" fullWidth />
                </div>
              )}

              {/* List Articles — BLURRED */}
              <div style={activeEditorialBlur}>
                <div className="border-b border-black pb-2 mb-6">
                  <h3
                    className="text-[11px] uppercase tracking-[0.2em] text-black"
                    style={{ fontFamily: 'system-ui, -apple-system, sans-serif', fontWeight: 600 }}
                  >
                    More to Read
                  </h3>
                </div>
                <div className="divide-y divide-black/5">
                  {config.listArticles.map((article, i) => (
                    <article key={i} className="group cursor-pointer py-4 first:pt-0">
                      <div className="flex items-start gap-4">
                        <span
                          className="text-[10px] uppercase tracking-[0.2em] text-black/30 w-24 flex-shrink-0 pt-1"
                          style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                        >
                          {article.category}
                        </span>
                        <div>
                          <h4 className="text-base leading-[1.35] text-black" style={{ fontWeight: 400 }}>
                            {article.title}
                          </h4>
                          <p
                            className="text-[11px] text-black/40 mt-1"
                            style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                          >
                            {article.author}
                          </p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar — 1/3 */}
            <aside className="lg:col-span-1 space-y-8">
              {/* 300×250 — hidden when no tag */}
              {(getSlot('300x250').tag || isEditing) && (
                <div id="adslot-300x250">
                  <GalleryAdSlot config={getSlot('300x250')} onConfigureTag={handleConfigureTag} isEditing={isEditing} className="adslot-300x250" />
                </div>
              )}

              {/* 300×600 */}
              {(!isWallpaper || getSlot('300x600').tag) && (
                <div id="adslot-300x600">
                  <GalleryAdSlot config={getSlot('300x600')} onConfigureTag={handleConfigureTag} isEditing={isEditing} className="adslot-300x600" />
                </div>
              )}

              {/* Newsletter Signup - Minimal — BLURRED */}
              <div className="border border-black/10 p-6" style={activeEditorialBlur}>
                <h4
                  className="text-[11px] uppercase tracking-[0.2em] text-black mb-1"
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif', fontWeight: 600 }}
                >
                  Newsletter
                </h4>
                <p className="text-sm text-black/50 leading-relaxed mb-4" style={{ fontWeight: 400 }}>
                  The best stories, delivered weekly to your inbox.
                </p>
                <input
                  type="email"
                  placeholder="Your email address"
                  className="w-full px-3 py-2.5 text-sm border border-black/15 bg-white placeholder:text-black/30 text-black focus:outline-none focus:border-black/40 transition-colors mb-3"
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                  readOnly
                />
                <button
                  className="w-full px-3 py-2.5 text-[11px] uppercase tracking-[0.15em] bg-black text-white hover:bg-black/80 transition-colors"
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif', fontWeight: 500 }}
                >
                  Subscribe
                </button>
              </div>

              {/* Sidebar Articles — BLURRED */}
              <div style={activeEditorialBlur}>
                <div className="border-b border-black pb-2 mb-4">
                  <h4
                    className="text-[11px] uppercase tracking-[0.2em] text-black"
                    style={{ fontFamily: 'system-ui, -apple-system, sans-serif', fontWeight: 600 }}
                  >
                    Most Popular
                  </h4>
                </div>
                <div className="divide-y divide-black/5">
                  {config.listArticles.slice(0, 4).map((article, i) => (
                    <article key={i} className="group cursor-pointer py-3 first:pt-0">
                      <div className="flex items-start gap-3">
                        <span
                          className="text-2xl text-black/10 leading-none mt-0.5"
                          style={{ fontFamily: 'Georgia, serif', fontWeight: 400 }}
                        >
                          {i + 1}
                        </span>
                        <div>
                          <h5 className="text-sm leading-[1.4] text-black" style={{ fontWeight: 400 }}>
                            {article.title}
                          </h5>
                          <p
                            className="text-[10px] text-black/30 mt-1"
                            style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                          >
                            {article.author}
                          </p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>

      )}

      {/* Footer 1×1 — adhesion/sticky-footer, last child of root div.
          Ad uses position:fixed internally and sticks to the publisher iframe viewport. */}
      <div id="adslot-footer-1x1">
        <GalleryAdSlot config={getSlot('footer-1x1')} onConfigureTag={handleConfigureTag} isEditing={isEditing} className="adslot-footer-1x1" />
      </div>

    </div>
  );

  // Frame mode — just the publisher site, no chrome (loaded inside iframe for viewport simulation)
  // overscroll-behavior:contain prevents scroll from chaining up to the outer page
  if (isFrame) {
    return <div className="bg-white" style={{ overscrollBehavior: 'contain' }}>{publisherSite}</div>;
  }

  // Full screen mode — just the publisher site, no chrome
  if (isFullScreen) {
    return (
      <div className="min-h-screen bg-white">
        {publisherSite}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0f]">
      <Header />

      {/* Gallery Toolbar + Publisher Preview Section — fills full viewport height */}
      <section id="gallery-preview" className="relative flex flex-col overflow-hidden" style={{ height: '100dvh' }}>

        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-50/50 to-slate-50 dark:via-slate-900/30 dark:to-slate-900/50" />

        <div className="relative flex flex-col flex-1 min-h-0 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-24 lg:pt-28 pb-4">
          <div className="flex-none">
            <GalleryToolbar
              isEditing={isEditing}
              onToggleEditing={() => setIsEditing(!isEditing)}
              activeTemplate={activeTemplate}
              onTemplateChange={setActiveTemplate}
              adSlots={adSlots}
              onUpdateTag={handleUpdateTag}
              onResetAll={handleResetAll}
              externalEditSlotId={externalEditSlotId}
              onClearExternalEdit={handleClearExternalEdit}
              previewMode
              deviceMode={deviceMode}
              onDeviceChange={setDeviceMode}
              onFullScreen={handleFullScreen}
            />
          </div>

          {/* Browser Frame — width controlled by device mode, height fills remaining space */}
          <div className="flex-1 min-h-0 mt-4 flex justify-center">
            <div
              className="w-full flex flex-col min-h-0 transition-all duration-500 ease-in-out"
              style={{ maxWidth: deviceWidth }}
            >
              <div className="relative flex flex-col flex-1 min-h-0">
                <div className="absolute -inset-3 bg-gradient-to-r from-[#635bff]/10 via-[#00d4ff]/10 to-[#ff80b5]/10 dark:from-[#635bff]/20 dark:via-[#00d4ff]/15 dark:to-[#ff80b5]/15 rounded-3xl blur-2xl opacity-60" />

                <div className="relative flex flex-col flex-1 min-h-0 bg-white rounded-2xl shadow-2xl shadow-slate-900/10 dark:shadow-black/40 border border-slate-200/80 dark:border-slate-700/50 overflow-hidden">
                  {/* Browser Title Bar */}
                  <div className="flex-none flex items-center gap-3 px-4 py-3 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                      <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
                      <div className="w-3 h-3 rounded-full bg-[#28c840]" />
                    </div>
                    <div className="flex-1 flex items-center justify-center">
                      <div className="flex items-center gap-2 px-4 py-1.5 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 max-w-lg w-full">
                        <svg className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          www.{config.domain}
                        </span>
                      </div>
                    </div>
                    <div className="w-[52px]" />
                  </div>

                  {/* ===== PUBLISHER SITE (in iframe for accurate viewport simulation) ===== */}
                  <iframe
                    ref={iframeRef}
                    src={`${window.location.pathname}?frame=true`}
                    title="Publisher preview"
                    className="border-0 w-full flex-1 min-h-0 block"
                    onLoad={sendStateToFrame}
                  />
                  {/* ===== END PUBLISHER SITE ===== */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#635bff] via-[#7c3aed] to-[#635bff]">
          <div className="absolute inset-0 grid-pattern opacity-10" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
            Ready to create ads that captivate?
          </h2>
          <p className="mt-6 text-lg text-white/80 max-w-2xl mx-auto">
            Join leading publishers and brands using Adventive to build high-impact interactive ads that drive real engagement and revenue.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/demo')}
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-[#635bff] bg-white rounded-full hover:bg-slate-50 transition-colors group shadow-xl shadow-black/20"
            >
              Request a Demo
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/platform')}
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-white/10 backdrop-blur-sm border border-white/20 rounded-full hover:bg-white/20 transition-all"
            >
              Explore the Platform
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GalleryPreview;
